import { useBrand } from '@bnnvara/brand';
import get from 'lodash/get';
import sortBy from 'lodash/sortBy';
import PropTypes from 'prop-types';
import React from 'react';

import { FetchArticlesAndPages } from '../../../api/queries/articlesandpages.gql';
import { FetchMediaList } from '../../../api/queries/media.gql';
import { FetchMixedMedia } from '../../../api/queries/mixedmedia.gql';
import { FetchSlides } from '../../../api/queries/slides.gql';
import { FetchTeaser } from '../../../api/queries/teaser.gql';
import TYPES from '../../../composable-components/ComposableCardList/enum/cardListContentTypes';
import PageQuery from '../../PageQuery';

const typeConfig = {
  [TYPES.media]: {
    query: FetchMediaList,
    itemsPath: 'brand.mediaList.edges',
    moreItemsPath: 'brand.mediaList.pageInfo',
    createQueryVariables: (variables, brand) => ({
      brandSlug: brand.slug,
      paginator: {
        ...variables,
      },
    }),
  },

  [TYPES.mixed]: {
    query: FetchMixedMedia,
    itemsPath: 'brand.mediaList.edges',
    itemsPath2: 'brand.pages.edges',
    moreItemsPath: 'brand.mediaList.edges',
    createQueryVariables: (variables, brand) => ({
      brandSlug: brand.slug,
      ...variables,
    }),
  },

  [TYPES.teaser]: {
    query: FetchTeaser,
    itemsPath: 'brand.teaser.items',
    createQueryVariables: (variables, brand) => ({
      brandSlug: brand.slug,
      ...variables,
    }),
  },

  [TYPES.slide]: {
    query: FetchSlides,
    headingPath: 'brand.slider.title',
    itemsPath: 'brand.slides.edges',
    createQueryVariables: ({ slug, limit }, brand) => ({
      brandSlug: brand.slug,
      slug,
      paginator: {
        limit,
      },
    }),
  },

  [TYPES.brand]: {
    query: FetchSlides,
    headingPath: 'brand.slider.title',
    itemsPath: 'brand.slides.edges',
    createQueryVariables: ({ slug, limit }, brand) => ({
      brandSlug: brand.slug,
      slug,
      paginator: {
        limit,
      },
    }),
  },

  [TYPES.article]: {
    query: FetchArticlesAndPages,
    itemsPath: 'brand.pages.edges',
    moreItemsPath: 'brand.pages.pageInfo',
    createQueryVariables: (variables, brand) => ({
      brandSlug: brand.slug,
      type: 'article',
      paginator: {
        ...variables,
      },
    }),
  },
};

const CardListContainer = ({ contentType, variables, children }) => {
  const brand = useBrand();
  const {
    query,
    itemsPath,
    itemsPath2,
    moreItemsPath,
    createQueryVariables,
    headingPath,
  } = typeConfig[contentType];

  const queryVariables = createQueryVariables(variables, brand);

  return (
    <PageQuery query={query} variables={queryVariables}>
      {({ data }) => {
        const getItemsPath = get(data, itemsPath, []);
        const getItemsPath2 = get(data, itemsPath2, []);
        const unsortedItems = [...getItemsPath, ...getItemsPath2];
        const unfilteredItems = sortBy(unsortedItems, [
          'publishDate',
        ]).reverse();
        let allItems = unfilteredItems;

        if (variables.allowedTypes) {
          allItems = unfilteredItems.filter((item) => {
            // eslint-disable-next-line no-underscore-dangle
            return variables.allowedTypes.includes(item.__typename);
          });
        }

        let items = allItems;

        if (variables.limit) {
          items = allItems.slice(0, variables.limit);
        }

        const heading = get(data, headingPath, null);
        const { hasNextPage } = get(data, moreItemsPath, []);

        return React.cloneElement(children, {
          items,
          hasNextPage,
          heading,
        });
      }}
    </PageQuery>
  );
};

CardListContainer.propTypes = {
  contentType: PropTypes.oneOf(Object.keys(typeConfig)).isRequired,
  children: PropTypes.node,
  variables: PropTypes.shape({
    limit: PropTypes.number,
    label: PropTypes.string,
    allowedTypes: PropTypes.arrayOf(PropTypes.string),
  }),
};

CardListContainer.defaultProps = {
  children: null,
  variables: {},
};

export default CardListContainer;
